/**
 * ProjectCard Component
 *
 * Displays project information in a structured card format.
 * Designed for use in dropdowns, lists, and selection interfaces.
 *
 * Parses project name strings into structured parts for better display:
 * - Code: The project identifier (e.g., "TLB-0197-16")
 * - Client: Company/client name (e.g., "Aker BP")
 * - Category: Project type (e.g., "IO Reports")
 * - Request ID: Task identifier (e.g., "Request #16")
 * - Title: Main description
 * - Change number & sub-description (for detailed view)
 *
 * @example
 * ```tsx
 * <ProjectCard
 *   code="TLB-0197-16"
 *   name="AKER BP - IO REPORTS - REQUEST #16 - CODE MANAGEMENT"
 *   variant="default"
 *   onClick={() => handleSelect(project)}
 * />
 * ```
 */

import React, { useMemo } from "react";
import classNames from "classnames";
import { parseProjectName } from "@/utils/projectParser";
import type { ProjectCardProps } from "./types";
import styles from "./ProjectCard.module.css";

export const ProjectCard: React.FC<ProjectCardProps> = ({
  code,
  name,
  parsed: parsedProp,
  knownCustomers,
  variant = "default",
  selected = false,
  disabled = false,
  onClick,
  className,
  "data-testid": testId,
}) => {
  // Parse project name if not provided
  const parsed = useMemo(() => {
    return parsedProp ?? parseProjectName(code, name, knownCustomers);
  }, [code, name, parsedProp, knownCustomers]);

  // Only show code badge if it's a real project code (not just "-" or empty)
  // A real code typically has alphanumeric characters
  const hasValidCode =
    parsed.code && parsed.code !== "-" && /[a-zA-Z0-9]/.test(parsed.code);

  const handleClick = () => {
    if (!disabled && onClick) {
      onClick();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.key === "Enter" || e.key === " ") && !disabled && onClick) {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div
      className={classNames(styles.card, className)}
      data-variant={variant}
      data-selected={selected ? "true" : undefined}
      data-disabled={disabled ? "true" : undefined}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="option"
      aria-selected={selected}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      data-testid={testId}
    >
      {/* Header row with code and meta info - only show if there's valid content */}
      {(hasValidCode ||
        parsed.client ||
        (parsed.category && variant !== "compact") ||
        (parsed.requestId && variant !== "compact")) && (
        <div className={styles.header}>
          {hasValidCode && <span className={styles.code}>{parsed.code}</span>}
          {parsed.client && (
            <span className={styles.client}>{parsed.client}</span>
          )}
          {parsed.category && variant !== "compact" && (
            <span className={styles.category}>{parsed.category}</span>
          )}
          {parsed.requestId && variant !== "compact" && (
            <span className={styles.requestId}>{parsed.requestId}</span>
          )}
        </div>
      )}

      {/* Title */}
      <span className={styles.title}>{parsed.title}</span>

      {/* Detailed variant extras */}
      {variant === "detailed" &&
        (parsed.changeNumber || parsed.subDescription) && (
          <div className={styles.metaRow}>
            {parsed.changeNumber && (
              <span className={styles.changeNumber}>{parsed.changeNumber}</span>
            )}
            {parsed.subDescription && (
              <span className={styles.subDescription}>
                {parsed.subDescription}
              </span>
            )}
          </div>
        )}
    </div>
  );
};

ProjectCard.displayName = "ProjectCard";

export default ProjectCard;
